import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cbnoEligibilityRules } from "features/subscription-process/home-insurance/components/real-estate/RealEstateEligibity";
import { fetchSurveyByContextId } from "features/subscription-process/slices/thunks/fetchSurveyByContextId";
import { nextStep } from "features/subscription-process/slices/thunks/nextStepThunk";
import { EligibilityRule, GenericProfilingConfig, GenericQuestionGroup, PossibleAnswer } from "../types/interfaces";
import { ProfilingMode, SurveyDirection } from "./types/enums";
import { debugConstraints, defaultAnswersOf, ensureValidCurrentStep, findNextValidStep, updateCurrentStepInfos, updateEligibility, updateVisibleSteps } from "./utils/profilingUtils";

export interface GenericProfilingState {
  loading: boolean;
  currentStep: number;
  currentStepInfos?: {
    title?: string;
    iconKey?: string;
  };
  answers: Record<string, PossibleAnswer[]>;
  externalAnswers: Record<string, PossibleAnswer[]>;
  profilingConfig: GenericProfilingConfig;
  isCompleted: boolean;
  eligibilityResult?: { isEligible: boolean; rule?: EligibilityRule };
  visibleSteps: GenericQuestionGroup[];
}

export const initialState: GenericProfilingState = {
  loading: false,
  currentStep: 1,
  answers: {},
  externalAnswers: {},
  profilingConfig: {
    questionsGroups: [],
    validationRules: [],
    mode: ProfilingMode.AutoAdvance,
    constraintsEnabled: false
  },
  isCompleted: false,
  visibleSteps: []
};

const genericProfilingSlice = createSlice({
  name: 'profiling',
  initialState,
  reducers: {
    initProfilingConfig: (state, action: PayloadAction<GenericProfilingConfig>) => {
      state.profilingConfig = action.payload;
      state.currentStep = 1;
      // Conserver les réponses externes lors de l'init
      state.answers = { ...state.externalAnswers };
      state.isCompleted = false;
      state.eligibilityResult = undefined;

      // Mettre à jour les étapes visibles et les infos
      updateVisibleSteps(state);
      updateCurrentStepInfos(state);
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      // Vérifier que le step demandé est dans les steps visibles
      const isValidStep = state.visibleSteps.some(s => s.step === action.payload);
      if (isValidStep) {
        state.currentStep = action.payload;
        updateCurrentStepInfos(state);
      }
    },

    goToFirstStep: (state) => {
      state.currentStep = 1;
      updateVisibleSteps(state);
      updateCurrentStepInfos(state);
    },

    goToNextStep: (state) => {
      const nextStep = findNextValidStep(state, SurveyDirection.Next);
      if (nextStep !== null) {
        debugConstraints('Navigation manuelle vers step suivant', { from: state.currentStep, to: nextStep });
        state.currentStep = nextStep;
        updateCurrentStepInfos(state);
      } else {
        debugConstraints('Pas de step suivant disponible', {});
      }
    },

    goToPreviousStep: (state) => {
      const previousStep = findNextValidStep(state, SurveyDirection.Previous);
      if (previousStep !== null) {
        debugConstraints('Navigation manuelle vers step précédent', { from: state.currentStep, to: previousStep });
        state.currentStep = previousStep;
        updateCurrentStepInfos(state);
      } else {
        debugConstraints('Pas de step précédent disponible', {});
      }
    },
    setProfilingMode: (state, action: PayloadAction<ProfilingMode>) => {
      state.profilingConfig = {
        ...state.profilingConfig,
        mode: action.payload
      };
    },
    setAnswer: (state, action: PayloadAction<{ questionKey: string, value: PossibleAnswer[] }>) => {
      const { questionKey, value } = action.payload;

      // Mettre à jour la réponse
      state.answers[questionKey] = value;

      // Recalculer les étapes visibles
      updateVisibleSteps(state);

      // Vérifier l'éligibilité
      updateEligibility(state);

      // S'assurer que le step courant est valide
      ensureValidCurrentStep(state);

      // Mettre à jour les infos du step
      updateCurrentStepInfos(state);

      // Auto-advance logic APRÈS avoir mis à jour les steps visibles
      if (state.profilingConfig.mode === ProfilingMode.AutoAdvance) {
        const currentGroup = state.visibleSteps.find(g => g.step === state.currentStep);
        if (currentGroup && currentGroup.questions.length === 1) {
          // Ne pas auto-avancer si la question est de type input (nécessite validation manuelle)
          const currentQuestion = currentGroup.questions[0];
          const hasInputAnswer = currentQuestion.possibleAnswers.some(
            (answer: any) => answer?.type === 'input' || answer?.type === 'inputWithUnit'
          );

          if (hasInputAnswer) {
            debugConstraints('Auto-advance désactivé pour question input', { step: state.currentStep });
            return;
          }

          const nextStep = findNextValidStep(state, SurveyDirection.Next);
          if (nextStep !== null) {
            debugConstraints('Auto-advance', { from: state.currentStep, to: nextStep });
            state.currentStep = nextStep;
            updateCurrentStepInfos(state);
          } else {
            // Plus d'étapes, vérifier l'éligibilité finale
            debugConstraints('Auto-advance: fin du questionnaire', {});
          }
        }
      }
    },
    setMultipleAnswers: (state, action: PayloadAction<Record<string, PossibleAnswer[]>>) => {
      state.answers = { ...state.answers, ...action.payload };
      updateVisibleSteps(state);
      updateEligibility(state);
    },
    removeAnswer: (state, action: PayloadAction<string>) => {
      delete state.answers[action.payload];
      updateVisibleSteps(state);
      updateEligibility(state);
    },
    resetProfiling: (state) => {
      state.currentStep = 1;
      // Réinitialiser les réponses externes et les answers
      state.externalAnswers = {};
      state.answers = {};
      state.isCompleted = false;
      state.loading = false;
      state.eligibilityResult = undefined;
      updateVisibleSteps(state);
      updateCurrentStepInfos(state);
    },
    markAsCompleted: (state) => {
      state.isCompleted = true;
    },
    // Nouvelles actions pour la gestion des contraintes
    enableConstraints: (state) => {
      state.profilingConfig.constraintsEnabled = true;
      updateVisibleSteps(state);
      updateCurrentStepInfos(state);
    },
    disableConstraints: (state) => {
      state.profilingConfig.constraintsEnabled = false;
      updateVisibleSteps(state);
      updateCurrentStepInfos(state);
    },
    // Actions pour les réponses externes (pré-remplies depuis l'extérieur)
    setExternalAnswer: (state, action: PayloadAction<{ questionKey: string, value: PossibleAnswer[] }>) => {
      const { questionKey, value } = action.payload;
      // S'assurer que les objets existent
      if (!state.externalAnswers) state.externalAnswers = {};
      if (!state.answers) state.answers = {};

      state.externalAnswers[questionKey] = value;
      // Les réponses externes sont aussi ajoutées aux answers pour les contraintes
      state.answers[questionKey] = value;
      updateVisibleSteps(state);
      updateEligibility(state);
      ensureValidCurrentStep(state);
      updateCurrentStepInfos(state);
    },
    setMultipleExternalAnswers: (state, action: PayloadAction<Record<string, PossibleAnswer[]>>) => {
      state.externalAnswers = { ...state.externalAnswers, ...action.payload };
      state.answers = { ...state.answers, ...action.payload };
      updateVisibleSteps(state);
      updateEligibility(state);
      ensureValidCurrentStep(state);
      updateCurrentStepInfos(state);
    },
    clearExternalAnswer: (state, action: PayloadAction<string>) => {
      delete state.externalAnswers[action.payload];
      delete state.answers[action.payload];
      updateVisibleSteps(state);
      updateEligibility(state);
    },
    clearAllExternalAnswers: (state) => {
      const externalKeys = Object.keys(state.externalAnswers);
      externalKeys.forEach(key => {
        delete state.answers[key];
      });
      state.externalAnswers = {};
      updateVisibleSteps(state);
      updateEligibility(state);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(nextStep.pending, (state) => {
        state = initialState
      })
      .addCase(fetchSurveyByContextId.fulfilled, (state, action) => {
        if (action.payload) {
          state.profilingConfig = {
            ...state.profilingConfig,
            questionsGroups: action.payload.questionsGroups,
            // Règles du back quand le questionnaire en porte (COPRO_MRI…) ; à défaut les
            // règles CBNO codées en dur, tant que celles-ci ne sont pas migrées en base.
            eligibilityRules: action.payload.eligibilityRules.length > 0
              ? action.payload.eligibilityRules
              : cbnoEligibilityRules,
            mode: action.payload.profilingMode,
            constraintsEnabled: true // FORCER l'activation
          }
          state.currentStep = 1;
          state.answers = defaultAnswersOf(action.payload.questionsGroups);
          state.isCompleted = false;
          state.eligibilityResult = undefined;

          // Mettre à jour les étapes visibles et les infos
          updateVisibleSteps(state);
          updateCurrentStepInfos(state);
        }
      })
  },
});

export const {
  initProfilingConfig,
  setCurrentStep,
  goToNextStep,
  goToPreviousStep,
  setAnswer,
  setMultipleAnswers,
  removeAnswer,
  resetProfiling,
  markAsCompleted,
  enableConstraints,
  disableConstraints,
  setProfilingMode,
  goToFirstStep,
  setExternalAnswer,
  setMultipleExternalAnswers,
  clearExternalAnswer,
  clearAllExternalAnswers
} = genericProfilingSlice.actions;

export default genericProfilingSlice.reducer;
